import { SafePromise } from '@benzinga/safe-await';
import { ExtendedSubscribable, Subscription } from '@benzinga/subscribable';

import {
  Financials,
  FinancialsQuery,
  Ownership,
  Peer,
  // EarningReport,
  // OperationRatio,
  // ValuationRatio,
  ValuationRatiosQuery,
} from './entities';
import { SecuritiesRequest, FinancialsRequestEvent } from './request';
import { SecuritiesStore } from './store';
import { Session, StockSymbol } from '@benzinga/session';
import { InsidersFiling } from '@benzinga/insider-trades-manager';
import { oneRequestAtATimeMultipleArgs } from '@benzinga/utils';

interface SecuritiesFunctions {
  getFundamentals: SecuritiesManager['getFundamentals'];
  getFinancials: SecuritiesManager['getFinancials'];
  getOwnership: SecuritiesManager['getOwnership'];
}

interface FundamentalsUpdateEvent {
  fundamentals: Financials[];
  query: FinancialsQuery;
  type: 'fundamentals_updated';
}

interface FinancialsUpdateEvent {
  financials: Financials[];
  query: FinancialsQuery;
  type: 'financials_updated';
}

interface ValuationRatioUpdateEvent {
  valuationRatios: Financials[];
  type: 'valuation_ratios_updated';
}

interface OperationRatioUpdateEvent {
  operationRatios: Financials[];
  type: 'operation_ratios_updated';
}

interface EarningReportsUpdateEvent {
  //earningReports: EarningReport[];
  earningReports: Financials[];
  type: 'earning_reports_updated';
}

export type SecuritiesManagerEvent =
  | FinancialsRequestEvent
  | FinancialsUpdateEvent
  | FundamentalsUpdateEvent
  | ValuationRatioUpdateEvent
  | OperationRatioUpdateEvent
  | EarningReportsUpdateEvent;

/**
 * Static-like securities data manager
 *
 * To use, obtain an instance by calling session.getManager(SecuritiesManager)
 *
 * @export
 * @class SecuritiesManager
 * @extends {ExtendedSubscribable<SecuritiesManagerEvent, SecuritiesFunctions>}
 */
export class SecuritiesManager extends ExtendedSubscribable<SecuritiesManagerEvent, SecuritiesFunctions> {
  private store: SecuritiesStore;
  private request: SecuritiesRequest;
  private requestSubscription?: Subscription<SecuritiesRequest>;
  private requestFundamentals;
  private requestFinancials;
  private requestOwnership;
  private requestPeers;
  private requestInsiders;

  constructor(session: Session) {
    super();
    this.request = new SecuritiesRequest(session);
    this.store = new SecuritiesStore();
    this.requestFundamentals = oneRequestAtATimeMultipleArgs(this.request.getFundamentals);
    this.requestFinancials = oneRequestAtATimeMultipleArgs(this.request.getFinancials);
    this.requestOwnership = oneRequestAtATimeMultipleArgs(this.request.getOwnership);
    this.requestPeers = oneRequestAtATimeMultipleArgs(this.request.getPeers);
    this.requestInsiders = oneRequestAtATimeMultipleArgs(this.request.getInsiders);
  }

  /**
   * @internal
   *
   * @static
   * @memberof SecuritiesManager
   */
  public static getName = () => 'benzinga-securities';

  /**
   * Compares two financials by IDs
   *
   * Returns true if they are equal
   *
   * @static
   * @param {Financials[]} lhs
   * @param {Financials[]} rhs
   * @memberof SecuritiesManager
   */
  public static compareFinancials = (lhs: Financials[], rhs: Financials[]): boolean => {
    return lhs.every(financial => rhs.some(newFinancial => financial.id === newFinancial.id));
  };

  public getFundamentalsCached = (query: FinancialsQuery) => this.store.getFundamentals(query);
  public getFundamentals = async (query: FinancialsQuery): SafePromise<Financials[]> => {
    if (this.store.getFundamentals(query) === undefined) {
      const fundamentals = await this.requestFundamentals(query);
      if (fundamentals.ok) {
        if (this.store.setFundamentals(query, fundamentals.ok)) {
          this.dispatch({
            fundamentals: fundamentals.ok,
            query,
            type: 'fundamentals_updated',
          });
        }
      }
      return fundamentals;
    } else {
      return { ok: this.store.getFundamentals(query) ?? [] };
    }
  };

  /**
   * Fetches financials by given query
   *
   * Caches value when possible
   *
   * @param {FinancialsQuery} query
   * @memberof SecuritiesManager
   */
  public getFinancialsCached = (query: FinancialsQuery) => this.store.getFinancials(query);
  public getFinancials = async (query: FinancialsQuery): SafePromise<Financials[]> => {
    if (this.store.getFinancials(query) === undefined) {
      const financials = await this.requestFinancials(query);
      if (financials.ok) {
        if (this.store.setFinancials(query, financials.ok)) {
          this.dispatch({
            financials: financials.ok,
            query,
            type: 'financials_updated',
          });
        }
      }
      return financials;
    } else {
      return { ok: this.store.getFinancials(query) ?? [] };
    }
  };

  public getOwnershipCached = (symbol: StockSymbol) => this.store.getOwnership(symbol);
  public getOwnership = async (symbol: StockSymbol): SafePromise<Ownership | undefined> => {
    if (this.store.getOwnership(symbol) === undefined) {
      const ownership = await this.requestOwnership(symbol);
      if (ownership.ok) {
        this.store.setOwnership(symbol, ownership.ok);
      }
      return ownership;
    }
    return { ok: this.store.getOwnership(symbol) ?? undefined };
  };

  /** @deprecated Use ScannerManager.getRelatedSymbols or ScannerManager.getRelatedSymbolPeerQuotes instead. */
  public getPeersCached = (symbol: StockSymbol) => this.store.getPeers(symbol);

  /** @deprecated Use ScannerManager.getRelatedSymbols or ScannerManager.getRelatedSymbolPeerQuotes instead. */
  public getPeers = async (symbol: StockSymbol, fields: string[]): SafePromise<Peer[] | undefined> => {
    const cachedPeers = this.store.getPeers(symbol);

    if (!cachedPeers) {
      const peers = await this.requestPeers(symbol, fields);
      if (peers.ok) {
        this.store.setPeers(symbol, peers.ok);
      }
      return peers;
    }
    return { ok: cachedPeers };
  };

  public getInsidersCached = (symbol: StockSymbol) => this.store.getInsiders(symbol);
  public getInsiders = async (symbol: StockSymbol): SafePromise<InsidersFiling[] | undefined> => {
    if (this.store.getInsiders(symbol) === undefined) {
      const insiders = await this.requestInsiders(symbol);
      if (insiders.ok) {
        this.store.setInsiders(symbol, insiders.ok);
      }
      return insiders;
    }
    return { ok: this.store.getInsiders(symbol) ?? [] };
  };

  /**
   * Fetches valuationRations by given query
   *
   * Caches value when possible
   *
   * @param {ValuationRatiosQuery} query
   * @memberof SecuritiesManager
   */
  public getValuationRatios = async (query: ValuationRatiosQuery): SafePromise<Financials[]> => {
    if (this.store.getValuationRatios(query) === undefined) {
      const valuationRatios = await this.request.getValuationRatios(query);
      if (valuationRatios.ok) {
        if (this.store.setValuationRatios(query, valuationRatios.ok)) {
          this.dispatch({
            type: 'valuation_ratios_updated',
            valuationRatios: valuationRatios.ok,
          });
        }
      }
      return valuationRatios;
    } else {
      return { ok: this.store.getValuationRatios(query) ?? [] };
    }
  };

  /**
   * Fetches operationRatios by given query
   *
   * Caches value when possible
   *
   * @param {ValuationRatiosQuery} query
   * @memberof SecuritiesManager
   */
  public getOperationRatios = async (query: ValuationRatiosQuery): SafePromise<Financials[]> => {
    if (this.store.getOperationRatios(query) === undefined) {
      const operationRatios = await this.request.getOperationRatios(query);
      if (operationRatios.ok) {
        if (this.store.setOperationRatios(query, operationRatios.ok)) {
          this.dispatch({
            operationRatios: operationRatios.ok,
            type: 'operation_ratios_updated',
          });
        }
      }
      return operationRatios;
    } else {
      return { ok: this.store.getOperationRatios(query) ?? [] };
    }
  };

  /**
   * Fetches earningReports by given query
   *
   * Caches value when possible
   *
   * @param {ValuationRatiosQuery} query
   */
  public getEarningReports = async (query: ValuationRatiosQuery): SafePromise<Financials[]> => {
    if (this.store.getEarningReports(query) === undefined) {
      const earningReports = await this.request.getEarningReports(query);
      if (earningReports.ok) {
        if (this.store.setEarningReports(query, earningReports.ok)) {
          this.dispatch({
            earningReports: earningReports.ok,
            type: 'earning_reports_updated',
          });
        }
      }
      return earningReports;
    } else {
      return { ok: this.store.getEarningReports(query) ?? [] };
    }
  };

  protected onSubscribe(): SecuritiesFunctions {
    return {
      getFinancials: this.getFinancials,
      getFundamentals: this.getFundamentals,
      getOwnership: this.getOwnership,
    };
  }

  protected onFirstSubscription(): void {
    this.requestSubscription = this.request.listen(event => this.dispatch(event));
  }

  protected onZeroSubscriptions(): void {
    this.requestSubscription?.unsubscribe();
  }
}
